import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { useEditor } from '@toasttab/sites-components';
import classnames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { MenuConfig, MenuTemplate, PlaceHolderImageGenerationEnum, Theme } from 'src/apollo/sites';
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker';
import { formatImageURL, slugify } from 'src/lib/js/utilities';
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice';

import Image from 'shared/components/common/Image';
import { useModal } from 'shared/components/common/modal';
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext';
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext';
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext';

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme';
import ItemModal from 'public/components/default_template/online_ordering/item_modal/ItemModal';
import ReadOnlyItemModal from 'public/components/default_template/online_ordering/item_modal/ReadOnlyItemModal';
import { MenuItemTags, OfferBadge } from 'public/components/default_template/online_ordering/item_tags/MenuItemTags';
import { SearchHighlightedText } from 'public/components/default_template/search';
import { useCart } from 'public/components/online_ordering/CartContext';
import { useOffers } from 'public/components/online_ordering/OffersContext';
import { useOptionalTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext';
import { Channel } from 'public/js/siteUtilities';

import { DefaultPlaceHolderMenuImage } from './DefaultPlaceHolderMenuImage';
import { MenuItem } from './MenuSection';
import { TemplateOverrides } from './menuStylingOverrides';

export const CLICKED_MENU_ITEM_EVENT_NAME = 'Clicked menu item';
export const CLICKED_MENU_ITEM_EVENT_NO_NAME_STR = 'No name';

export const IMAGE_SUFFIX_REGEX = /\.(jpg|png)$/;

export type ToastImageSet = {
  xs?: string;
  small?: string;
  medium?: string;
  large?: string;
}

type MenuItemImageSet = {
  '140w'?: string
  '300w'?: string
  '600w'?: string
  '900w'?: string
}

// These source set URLs are served by the toast photo service
// and are guaranteed to exist
export const getMenuItemSrcSet = (images: ToastImageSet): MenuItemImageSet => {
  const imgSet: MenuItemImageSet = {};
  if(images.xs) { imgSet['140w'] = formatImageURL(images.xs); }
  if(images.small) { imgSet['300w'] = formatImageURL(images.small); }
  if(images.medium) { imgSet['600w'] = formatImageURL(images.medium); }
  if(images.large) { imgSet['900w'] = formatImageURL(images.large); }
  return imgSet;
};

export const getTemplateClass = (template: MenuTemplate | undefined | null): string => {
  if(!template) return '';
  const classStr = template ? template.toString() : '';
  if(!classStr) return '';
  return classStr.charAt(0).toLowerCase() + classStr.slice(1);
};

type Props = {
  item: MenuItem;
  menuGuid?: string | null;
  id?: string;
  setIsMapOrModalOpen?: (isOpen: boolean) => void;
  // True if the location map is open, or if any card has the modal open.
  isMapOrModalOpen?: boolean;
  // If true, formats the layout of the card as a popular item card.
  isPopularItem?: boolean;
  // True for ephemeral menu items that shouldn't be linked to.
  preventDeeplink?: boolean;
  isReadOnly?: boolean;
  menuConfig?: MenuConfig | null;
  rowHasDescription?: boolean;
  templateOverrides?: TemplateOverrides;
  channelGuid?: string;
}

type ItemContentProps = {
  item: MenuItem;
  menuGuid?: string | null;
  // If true, formats the layout of the card as a popular item card.
  isPopularItem?: boolean;
  menuConfig?: MenuConfig | null;
  rowHasDescription?: boolean;
  templateOverrides?: TemplateOverrides;
  isReadOnly?: boolean;
}

type Params = {
  itemSlug?: string;
  itemGuid?: string;
  guid?: string;
}

// React Router doesn't provide functionality for changing the current route
// without rerendering, so we use native browser functionality
const replaceUrl = (newUrl: string) => {
  window.history.replaceState(null, '', newUrl);
};

export const ItemContent = (props: ItemContentProps) => {
  const { item, isPopularItem, menuConfig, templateOverrides } = props;
  const { restaurant: { config, meta: { backgroundColor: pageBackgroundColor } } } = useRestaurant();
  const getColorFromTheme = useThemeColorScheme();
  const { timeBasedRulesMap } = useOptionalTimeBasedRules() || { timeBasedRulesMap: {} as NonNullable<ReturnType<typeof useOptionalTimeBasedRules>>['timeBasedRulesMap'] };
  const { itemOffers, getOffersTextForEntity } = useOffers();
  const numItemOffers = item.guid ? itemOffers[item.guid]?.length ?? 0 : 0;
  const totalOffers = getOffersTextForEntity(item.guid, item.itemGroupGuid, props.menuGuid).length;
  const mobileOptimized = useIsMobile(ScreenWidth.EXTRA_SMALL) && templateOverrides?.enableMobileOptimization;
  const backgroundColor = useMemo(() => {
    return getColorFromTheme(theme => {
      const surfaceDefault = theme.colorScheme.surface.default;
      const menuOverrides = props.isReadOnly ? theme.colorOverrides?.viewOnlyMenu : theme.colorOverrides?.onlineOrderingMenu;
      return mobileOptimized ? surfaceDefault : menuOverrides?.cardBackground ?? surfaceDefault;
    }, mobileOptimized ? pageBackgroundColor : menuConfig?.colors?.background);
  }, [getColorFromTheme, menuConfig?.colors?.background, mobileOptimized, pageBackgroundColor, props.isReadOnly]);

  const timeBasedRules = item.guid && timeBasedRulesMap[item.guid];
  const imgSrc = useMemo(() => item.imageUrls?.medium || item.imageUrls?.sm || item.imageUrls?.large || item.imageUrls?.xs, [item.imageUrls]);

  const menuTemplateConfig = templateOverrides?.menuTemplateOverride;
  const itemDescriptionTextColor = menuConfig?.colors?.secondaryText;
  const contentStyle = useMemo(() => {
    const legacyStyle = itemDescriptionTextColor ? { color: itemDescriptionTextColor } : {};

    return getColorFromTheme(theme => {
      const menuOverrides = props.isReadOnly ? theme.colorOverrides?.viewOnlyMenu : theme.colorOverrides?.onlineOrderingMenu;
      return { color: menuOverrides?.itemDescription ?? theme.colorScheme.text.secondary };
    }, legacyStyle);
  }, [getColorFromTheme, itemDescriptionTextColor, props.isReadOnly]);

  const ooBasic = config.isOnlineOrderingOnly && config.hasFullCustomization === false;
  const sideImage = !menuTemplateConfig || menuTemplateConfig === MenuTemplate.SideImage;
  const squareImage = sideImage && (ooBasic || menuConfig?.format?.squareImages);
  const imageRatioClass = templateOverrides?.imageRatio || (squareImage ? 'squareImage' : '');
  const hideDescription = templateOverrides?.hideMenuItemDescription || false;

  const itemImage = useMemo(() => {
    const className = classnames('itemImage', imageRatioClass, { hideImage: templateOverrides?.hideImages, outOfStock: item.outOfStock });
    if(item?.image) {
      return <Image alt="" src={item.image} className={className} data-testid={item.name + '-image'} />;
    } else if(imgSrc && item.imageUrls) {
      return <Image
        alt=""
        src={formatImageURL(imgSrc)}
        srcSet={getMenuItemSrcSet(item.imageUrls)}
        className={className}
        data-testid={item.name + 'image'} />;
    } else if(templateOverrides?.placeholderImageConfig.generatePlaceholderImage === PlaceHolderImageGenerationEnum.RepeatedText) {
      return (
        <div className={classnames(className, 'placeholderImg')}>
          <DefaultPlaceHolderMenuImage
            templateOverrides={templateOverrides} />
        </div>
      );
    } else {
      return null;
    }
  },
  [
    imageRatioClass,
    templateOverrides,
    item.outOfStock,
    item.image,
    item.imageUrls,
    item.name,
    imgSrc
  ]);

  // The following code is used to determine if the item tags should be hidden because they are pushing the itemInfo out of the container
  const itemInfoRef = useRef<HTMLDivElement>(null);
  const [hideMenuItemTags, setHideMenuItemTags] = useState(false);
  const [hasWindowResized, setHasWindowResized] = useState(false);

  useEffect(() => {
    // The timeout allows itemTags to calculate how many tags to show before we check the sizing
    setTimeout(() => {
      if(!hideMenuItemTags || hasWindowResized) {
        setHasWindowResized(false);
        const itemInfoEl = itemInfoRef.current;
        setHideMenuItemTags(!!itemInfoEl?.offsetLeft && itemInfoEl.offsetLeft - 16 < 0);
      }
    }, 200);
  }, [hasWindowResized, hideMenuItemTags]);

  // We use the timeoutRef to debounce the window resize event
  const timeOutRef = useRef<any >(false);
  // Everytime the window resizes, we want to show the item tags to see if they fit
  const showItemTags = useCallback(() => {
    if(timeOutRef.current !== false) {
      clearTimeout(timeOutRef.current);
    }
    timeOutRef.current = setTimeout(() => {
      setHideMenuItemTags(false);
      setHasWindowResized(true);
    }, 100);
  }, []);
  useEffect(() => {
    if(window) {
      window.addEventListener('resize', showItemTags);
    }
  }, [showItemTags]);

  if(isPopularItem) {
    const backgroundImage = !item.image && item.imageUrls?.raw ? formatImageURL(item.imageUrls.raw) : null;
    const popularItemBackground = backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : null;

    return (
      <div className={classnames('popularItem', popularItemBackground ? 'backgroundImage' : '' )} style={popularItemBackground ? popularItemBackground : undefined}>
        <div className="itemName">{props.item.name}</div>
      </div>
    );
  }
  if(menuTemplateConfig === MenuTemplate.Condensed || menuTemplateConfig === MenuTemplate.ExtraCondensed || menuTemplateConfig === MenuTemplate.Stacked) {
    return (
      <div className={classnames('itemInfo', getTemplateClass(menuTemplateConfig))} ref={itemInfoRef}>
        <div className="itemHeadAvailDesc">
          <div className="itemHeader">
            <h3 style={{ fontFamily: 'inherit', fontSize: 'inherit' }}>
              <SearchHighlightedText className="headerText" text={item.name ?? ''} />
            </h3>
          </div>
          {item.description && !hideDescription &&
            <div className="itemContent">
              <p style={contentStyle} className="itemDescription">
                <SearchHighlightedText text={item.description} />
              </p>
            </div>}
          <div className="priceAvailability">
            <PriceLine item={item} />
            {!hideMenuItemTags && <MenuItemTags item={item} backgroundColor={backgroundColor} />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {numItemOffers > 0 && !item.outOfStock && !mobileOptimized ?
        <OfferBadge
          text={totalOffers > 1 ? 'OFFERS' : 'OFFER'}
          className={classnames('offerBadge', menuTemplateConfig === MenuTemplate.LeftImageV2 ? 'leftAlign' : 'rightAlign')} /> :
        null}
      {itemImage}
      <div className={classnames('itemInfo', { outOfStock: item.outOfStock })} ref={itemInfoRef}>
        <div className={classnames('itemHeader', { hasTag: timeBasedRules })}>
          <h3 style={{ fontFamily: 'inherit', fontSize: 'inherit' }}>
            <SearchHighlightedText className="headerText" text={item.name ?? ''} />
          </h3>
          {/* Description added to the header so we can clamp ellipsis to 3 lines as a group */}
          {item.description && !hideDescription &&
            <div className="itemContent">
              <p data-testid="item-content-description" style={contentStyle} className="itemDescription">
                <SearchHighlightedText text={item.description ?? ''} />
              </p>
            </div>}
        </div>
        <div className="priceAvailability">
          <PriceLine item={item} />
          {!hideMenuItemTags && <MenuItemTags item={item} backgroundColor={backgroundColor} numOffers={mobileOptimized && numItemOffers > 0 ? totalOffers : 0} />}
        </div>
      </div>
    </>
  );
};

export const PriceLine = ({ item }: {item: MenuItem}) => {
  const perUnitString = item.usesFractionalQuantity && item.unitOfMeasure ? `/${item.unitOfMeasure.toLowerCase()}` : '';
  if(!item.price && !item.prices?.length) {
    return null;
  }
  const value = item.price ?? [...item.prices || []].sort((a, b) => a - b)[0] ?? 0;
  const defaultModifiersPrices = item.defaultModifiersPrice || 0;
  const unit = item.price && item.prices?.length && item.prices.length > 1 ? '+' : '';
  return (
    <span className={classnames('price', { outOfStock: item.outOfStock })} data-testid={`price-${item.guid}`}>
      <FormattedPrice value={value + defaultModifiersPrices} />
      {unit}{perUnitString}
    </span>
  );
};

const MenuItemCard = (props: Props) => {
  const { isEditor } = useEditor();

  return isEditor
    ? <MenuItemCardBase {...props} preventDeeplink={true} disableClickEvents={true} />
    : <MenuItemCardWithRouterParamsAndTracker {...props} />;
};

const MenuItemCardWithRouterParamsAndTracker = (props: Props) => {
  const params = useParams<Params>();
  const { track } = useThrottledTracker();
  return <MenuItemCardBase {...props} track={track} params={params} />;
};

type BaseProps = Props & {
  track?: (name: any, data: any) => void
  params?: Params
  disableClickEvents?: boolean
};

const menuItemStyle = (item: MenuItem, menuConfig?: MenuConfig | null, theme?: Theme | undefined | null, isReadOnly: boolean = false) => {
  if(theme?.enabled) {
    const menuColorOverrides = isReadOnly ? theme.colorOverrides?.viewOnlyMenu : theme.colorOverrides?.onlineOrderingMenu;
    return {
      ...menuColorOverrides?.cardBackground && { backgroundColor: menuColorOverrides?.cardBackground },
      ...menuColorOverrides?.itemTitle && !item.outOfStock && { color: menuColorOverrides?.itemTitle }
    };
  } else {
    return {
      ...menuConfig?.colors?.background && { backgroundColor: menuConfig.colors.background },
      ...menuConfig?.colors?.primaryText && !item.outOfStock && { color: menuConfig.colors.primaryText }
    };
  }
};


const MenuItemCardBase = ({ disableClickEvents = false, ...props } : BaseProps) => {
  const track = props.track;
  const ref = useRef<HTMLLIElement>(null);
  const { item, setIsMapOrModalOpen, isReadOnly } = props;
  const { channel } = useCart();
  const { ooRestaurant, selectedLocation, restaurant: { theme } } = useRestaurant();
  const { orderPath, ecommPath } = useRestaurantRoutes();
  const { isOpen, onOpen, onClose } = useModal();
  const getColorFromTheme = useThemeColorScheme();
  const path = channel === Channel.ECOMM && ecommPath ? ecommPath : orderPath;
  const { ooEcommPdp: ooEcommPdpEnabled } = useFlags();

  const handleFocus = () => {
    if(ref.current) {
      const { top } = ref.current.getBoundingClientRect();
      // If the top of the element is less than 105px from the top of the window,
      // it's most likely obscured by the menu nav.  Scroll it into view.
      if(top <= 105) {
        // Use an offset of 260px to account for the global nav, menu nav, and potentially
        // the spotlight banner, which are present on scroll-up but not scroll-down
        window.scrollTo({ top: top + window.scrollY - 260 });
      }
    }
  };

  const openModal = useCallback(() => {
    onOpen();
    if(setIsMapOrModalOpen) {
      setIsMapOrModalOpen(true);
    }
  }, [onOpen, setIsMapOrModalOpen]);

  const closeModal = useCallback(() => {
    onClose();
    if(setIsMapOrModalOpen) {
      setIsMapOrModalOpen(false);
    }
    if(!isReadOnly) {
      replaceUrl(path);
    }
  }, [onClose, setIsMapOrModalOpen, isReadOnly, path]);

  const onItemClick = useCallback((e: React.MouseEvent) => {
    _onItemClickImpl(e, isReadOnly, item, track, path, openModal);
  }, [openModal, item, track, isReadOnly, path]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    onItemClickImpl(e, isReadOnly, item, track, path, openModal);
  }, [openModal, item, track, isReadOnly, path]);

  if(!item.name) {
    return null;
  }

  const itemStyle = menuItemStyle(item, props.menuConfig, theme, isReadOnly);

  const itemReference = !isReadOnly ? menuItemPath(channel !== Channel.ECOMM ? orderPath : ecommPath || '', item.name, item.guid) : undefined;
  const templateOverrides = props.templateOverrides;
  const menuTemplateConfig = templateOverrides?.menuTemplateOverride;
  const borderColor = getColorFromTheme(theme => {
    const menuOverrides = isReadOnly ? theme.colorOverrides?.viewOnlyMenu : theme.colorOverrides?.onlineOrderingMenu;
    return menuOverrides?.cardBorder ?? theme.colorScheme.border.default;
  }, templateOverrides?.borderColor);

  const itemContent = <ItemContent
    key={item.guid}
    item={item}
    menuGuid={props.menuGuid}
    isPopularItem={props.isPopularItem}
    menuConfig={props.menuConfig}
    rowHasDescription={props.rowHasDescription}
    templateOverrides={templateOverrides}
    isReadOnly={isReadOnly} />;

  return (
    <>
      {isReadOnly ?
        <ReadOnlyItemModal
          withTransitions
          isOpen={isOpen}
          onClose={closeModal}
          name={item.name}
          description={item.description}
          itemTags={item.itemTags}
          image={item?.image ? item.image : item.imageUrls?.raw ? item.imageUrls?.raw : undefined} />
        :
        <ItemModal
          withTransitions
          isOpen={isOpen}
          onClose={closeModal}
          restaurantGuid={selectedLocation.externalId}
          shortUrl={ooRestaurant?.shortUrl}
          specialRequestsEnabled={ooRestaurant?.specialRequestsConfig.enabled && channel !== Channel.ECOMM}
          specialRequestsPlaceholder={ooRestaurant?.specialRequestsConfig.placeholderMessage}
          itemGroupGuid={item.itemGroupGuid}
          itemMenuGuid={props.menuGuid}
          itemGuid={item.guid}
          itemTags={item.itemTags}
          channelGuid={props.channelGuid}
          shouldShowHighlights /> }
      {isReadOnly && (menuTemplateConfig === MenuTemplate.Condensed || menuTemplateConfig === MenuTemplate.ExtraCondensed || menuTemplateConfig === MenuTemplate.Stacked) ?
        <li role="listitem"
          key={item.guid || item.name}
          ref={ref}
          onFocus={handleFocus}
          style={{ ...itemStyle, pointerEvents: disableClickEvents ? 'none' : 'auto' }}
          className={classnames('item',
            templateOverrides?.cardOrientation,
            getTemplateClass(menuTemplateConfig))}>
          <ItemContent key={item.guid}
            item={item}
            menuGuid={props.menuGuid}
            isPopularItem={props.isPopularItem}
            menuConfig={props.menuConfig}
            templateOverrides={templateOverrides}
            isReadOnly={isReadOnly} />
        </li>
        :
        <li role="listitem"
          key={item.guid || item.name}
          ref={ref}
          onFocus={handleFocus}
          className={classnames('item',
            templateOverrides?.cardOrientation,
            templateOverrides?.backgroundShade,
            templateOverrides?.borderStroke,
            templateOverrides?.roundedCorner,
            templateOverrides?.dropShadow,
            getTemplateClass(menuTemplateConfig),
            {
              clickable: true,
              outOfStock: !!item.outOfStock,
              mobileOptimized: templateOverrides?.enableMobileOptimization
            })}
          style={{ ...itemStyle, borderColor, pointerEvents: disableClickEvents ? 'none' : 'auto' }}>
          {channel !== Channel.ECOMM || !ooEcommPdpEnabled ?
            <a
              data-testid={'add-to-cart-' + item.guid}
              href={itemReference}
              id={props.id}
              onClick={onItemClick}
              onKeyDown={onKeyDown}
              tabIndex={0}
              style={{ ...itemStyle, pointerEvents: disableClickEvents ? 'none' : 'auto' }}>
              {itemContent}
            </a>
            :
            <Link to={{ pathname: ecommMenuItemPath(ecommPath || '', item.itemGroupGuid, item.name || '', item.guid), state: { item } }}
              style={{ ...itemStyle, pointerEvents: disableClickEvents ? 'none' : 'auto' }}>
              {itemContent}
            </Link>}
        </li>}
    </>
  );
};

const _onItemClickImpl = (
  e: React.KeyboardEvent | React.MouseEvent, isReadOnly: boolean | undefined, item: MenuItem, track: ((name: any, data: any) => void) | undefined, orderPath: string, openModal : () => void
) => {
  e.preventDefault();
  e.stopPropagation();
  track?.(CLICKED_MENU_ITEM_EVENT_NAME, {
    guid: item.guid,
    name: item.name || CLICKED_MENU_ITEM_EVENT_NO_NAME_STR,
    readOnly: !!isReadOnly,
    outOfStock: !!item.outOfStock,
    hasImage: Boolean(item.image),
    descLength: item.description ? item.description.length : 0,
    timeBasedRule: item.timeBasedRules?.leadTimeRule ? 'Minimum lead time' : item.timeBasedRules?.preorderRule ? 'Preorder' : null
  });
  if(!item.outOfStock) {
    openModal();
    if(!isReadOnly) {
      replaceUrl(menuItemPath(orderPath, item.name || '', item.guid));
    }
  }
};

export const onItemClickImpl = (
  e: React.KeyboardEvent, isReadOnly: boolean | undefined, item: MenuItem, track: ((name: any, data: any) => void) | undefined, orderPath: string, openModal : () => void
) => {
  if(e.type === 'keydown' && (e.code !== 'Space' && e.code !== 'Enter')) {
    return; // only continue if the spacebar or enter key is pressed
  }
  _onItemClickImpl(e, isReadOnly, item, track, orderPath, openModal);
};


export const menuItemPath = (orderPath: string, itemName: string, itemGuid: string | null) => `${orderPath}/item-${slugify(itemName)}_${itemGuid}`;
export const ecommMenuItemPath = (orderPath: string, itemGroupGuid: string, itemName: string, itemGuid: string | null) =>
  `${orderPath}/group_${itemGroupGuid}/item-${slugify(itemName)}_${itemGuid}`;

export default MenuItemCard;
